import { useState, useContext, useEffect } from "react";
import { FornecedoresContext } from "../../../contexts/fornecedoresProvider";
import { UserContext } from "../../../contexts/userProvider";
import { BiX } from "react-icons/bi";
import BaseSelect from "../../General/FormsComp/Select/BaseSelect";

const ProdutoEdit = ({
  produto,
  salvarProduto,
  closeEditModal,
  marcas,
  modelos,
}) => {
  const { fornecedores } = useContext(FornecedoresContext);
  const { setAlert } = useContext(UserContext);
  
  // Estado local para gerenciar as mudanças sem fechar o modal
  const [produtoLocal, setProdutoLocal] = useState({
    ...produto,
    fornecedor: produto.fornecedor || null,
    marca: produto.marca || null,
    modelo: produto.modelo || null
  });
  
  const [newfornecedores, setNewFornecedores] = useState(null);
  const [newModelos, setNewModelos] = useState([]);
  const [newMarcas, setNewMarcas] = useState([]);

  useEffect(() => {
    let newFornecedores = fornecedores ? fornecedores : [];
    setNewFornecedores(newFornecedores);
  }, [fornecedores]);

  useEffect(() => {
    let newMarcas = marcas ? marcas : [];
    setNewMarcas(newMarcas);
  }, [marcas]);

  useEffect(() => {
    let newModelos = modelos ? modelos : [];
    setNewModelos(newModelos);
  }, [modelos]);

  // Atualizar estado local quando o produto prop mudar
  useEffect(() => {
    setProdutoLocal({
      ...produto,
      fornecedor: produto.fornecedor || null,
      marca: produto.marca || null,
      modelo: produto.modelo || null
    });
  }, [produto]);

  // Verificar se há mudanças não salvas
  const temMudancas = 
    produtoLocal.fornecedor !== produto.fornecedor ||
    produtoLocal.marca !== produto.marca ||
    produtoLocal.modelo !== produto.modelo;

  const handleSalvar = () => {
    salvarProduto(produtoLocal);
  };

  const handleSelectFornecedor = (e) => {
    const { value } = e.target;
    const fornecedorId = parseInt(value) || null;
    
    // Atualizar apenas o estado local
    setProdutoLocal(prevProduto => ({
      ...prevProduto,
      fornecedor: fornecedorId,
      // Limpar marca e modelo se o fornecedor mudar
      marca: prevProduto.fornecedor !== fornecedorId ? null : prevProduto.marca,
      modelo: prevProduto.fornecedor !== fornecedorId ? null : prevProduto.modelo
    }));
  };

  const handleSelectMarca = (e) => {
    const { value } = e.target;
    const marcaId = parseInt(value) || null;
    
    let marca = null;
    if (marcaId) {
      marca = newMarcas.find((marca) => marca.id === marcaId);
    }
    
    // Atualizar estado local
    setProdutoLocal(prevProduto => {
      const novoFornecedor = marca && marca.id_fornecedor !== prevProduto.fornecedor 
        ? marca.id_fornecedor 
        : prevProduto.fornecedor;
      
      // Verificar se o modelo atual pertence à nova marca
      const modeloAtualValido = prevProduto.modelo && marcaId &&
        newModelos.find(m => m.id === prevProduto.modelo && m.id_marca === marcaId);
        
      return {
        ...prevProduto,
        fornecedor: novoFornecedor,
        marca: marcaId,
        // Limpar modelo se marca mudou e modelo atual não é válido para a nova marca
        modelo: modeloAtualValido ? prevProduto.modelo : null
      };
    });
  };

  const handleSelectModelo = (e) => {
    const { value } = e.target;
    const modeloId = parseInt(value) || null;
    
    let modelo = null;
    let marcaDoModelo = null;
    
    if (modeloId) {
      modelo = newModelos.find((modelo) => modelo.id === modeloId);
      if (modelo) {
        marcaDoModelo = newMarcas.find(marca => marca.id === modelo.id_marca);
      }
    }
    
    // Atualizar estado local
    setProdutoLocal(prevProduto => ({
      ...prevProduto,
      fornecedor: marcaDoModelo ? marcaDoModelo.id_fornecedor : prevProduto.fornecedor,
      marca: modelo ? modelo.id_marca : prevProduto.marca,
      modelo: modeloId
    }));
  };

  // Mostrar todas as marcas, mas filtrar modelos pela marca selecionada
  const marcasFiltradas = newMarcas;
  const modelosFiltrados = produtoLocal.marca 
    ? newModelos.filter(modelo => modelo.id_marca === produtoLocal.marca)
    : newModelos;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={() => closeEditModal()}></div>
      
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-5xl h-[calc(78vh-5rem)] flex flex-col">
        <BiX onClick={() => closeEditModal()} className="absolute top-4 right-4 w-8 h-8 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer transition-colors duration-200 z-10" />
        
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white pr-10">Classificar Produto da Nota Fiscal</h1>
          {temMudancas && (
            <p className="text-base text-orange-600 dark:text-orange-400 mt-1">
              ⚠️ Há alterações não salvas
            </p>
          )}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            
            {/* Coluna 1: Informações do Produto Original */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Dados da Nota Fiscal
              </h3>
              
              <div className="space-y-2 text-base">
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Produto:</span>
                  <p className="text-gray-900 dark:text-white font-medium text-sm mt-1 break-words">{produtoLocal.nome}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Qtd:</span>
                    <p className="text-gray-900 dark:text-white">{produtoLocal.quantidade}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Total:</span>
                    <p className="text-gray-900 dark:text-white font-medium">{produtoLocal.valorTotal}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Coluna 2: Formulário de Edição */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Classificação do Produto
              </h3>

              {/* Fornecedor */}
              <div>
                <BaseSelect
                  label="Fornecedor *"
                  name="fornecedor"
                  value={produtoLocal.fornecedor || ''}
                  onChange={handleSelectFornecedor}
                  options={newfornecedores || []}
                  placeholder="Selecione o fornecedor"
                  valueKey="id"
                  labelKey="nome"
                  required
                />
              </div>

              {/* Marca */}
              <div>
                <BaseSelect
                  label="Marca *"
                  name="marca"
                  value={produtoLocal?.marca || ''}
                  onChange={handleSelectMarca}
                  options={marcasFiltradas || []}
                  placeholder="Selecione a marca"
                  valueKey="id"
                  labelKey="nome"
                  required
                />
              </div>

              {/* Modelo */}
              <div>
                <BaseSelect
                  label="Modelo *"
                  name="modelo"
                  value={produtoLocal.modelo || ''}
                  onChange={handleSelectModelo}
                  options={modelosFiltrados || []}
                  placeholder={!produtoLocal.marca ? "Selecione uma marca primeiro" : "Selecione o modelo"}
                  valueKey="id"
                  labelKey="nome"
                  required
                  disabled={!produtoLocal.marca}
                />
              </div>

              {/* Resumo Compacto */}
              {(produtoLocal.fornecedor || produtoLocal.marca || produtoLocal.modelo) && (
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 text-base">
                  <h4 className="font-medium text-green-900 dark:text-green-100 mb-2 text-sm">Selecionado:</h4>
                  <div className="space-y-1">
                    {produtoLocal.fornecedor && (
                      <p className="text-green-800 dark:text-green-200 text-sm truncate">
                        <strong>Fornecedor:</strong> {newfornecedores?.find(f => f.id === parseInt(produtoLocal.fornecedor))?.nome}
                      </p>
                    )}
                    {produtoLocal.marca && (
                      <p className="text-green-800 dark:text-green-200 text-sm truncate">
                        <strong>Marca:</strong> {newMarcas?.find(m => m.id === parseInt(produtoLocal.marca))?.nome}
                      </p>
                    )}
                    {produtoLocal.modelo && (
                      <p className="text-green-800 dark:text-green-200 text-sm truncate">
                        <strong>Modelo:</strong> {newModelos?.find(m => m.id === parseInt(produtoLocal.modelo))?.nome}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3 flex-shrink-0">
          <button
            onClick={() => closeEditModal()}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors duration-200 text-base"
          >
            Cancelar
          </button>
          <button
            onClick={handleSalvar}
            className={`px-4 py-2 font-medium rounded-lg transition-all duration-200 shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 text-base ${
              temMudancas 
                ? 'bg-orange-600 hover:bg-orange-700 text-white focus:ring-orange-500' 
                : 'bg-vox-green-600 hover:bg-vox-green-700 text-white focus:ring-vox-green-500'
            }`}
          >
            {temMudancas ? 'Salvar Alterações' : 'Salvar Classificação'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProdutoEdit;
